import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const Pricing = () => {
  const { t } = useTranslation('pricing');
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to format currency
  const formatPrice = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await api.get('/api/pricing');
        setPricingData(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching pricing:', error);
        setError('Failed to load pricing information');
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  // Helper function to format discount rates
  const renderDiscountItems = (discounts, type) => {
    return Object.entries(discounts)
      .sort(([keyA], [keyB]) => Number(keyA) - Number(keyB)) // Sort by threshold number
      .map(([threshold, discount]) => (
        <li key={`${type}-${threshold}`}>
          {t(`savings.${type}.description`, { 
            threshold: threshold,
            discount: discount
          })}
        </li>
      ));
  };

  if (loading) {
    return (
      <section className={theme.section}>
        <div className={theme.container}>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
            <div className="h-64 bg-gray-200 rounded-lg max-w-2xl mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !pricingData) {
    return (
      <section className={theme.section}>
        <div className={theme.container}>
          <p className="text-red-500 text-center">{error || 'Unable to load pricing'}</p>
        </div>
      </section>
    );
  }

  return (
    <section className={theme.section}>
      <div className={theme.container}>
        <h2 className={theme.text.h1 + ' mb-8 text-center'}>
          {t('title')}
        </h2>
        
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            <div className="space-y-6">
              {/* Base Animation Price */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('baseAnimation.title')}
                </h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-primary">
                    {formatPrice(pricingData.basePrice, pricingData.currency)}
                  </span>
                  <span className="ml-2 text-gray-500">{t('baseAnimation.perPhoto')}</span>
                </div>
                <p className="mt-2 text-gray-600">{t('baseAnimation.description')}</p>
              </div>

              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('additionalAnimations.title')}
                </h3>
                <div className="flex items-baseline">
                  <span className="text-4xl font-bold text-primary">
                    {formatPrice(pricingData.variantPrice, pricingData.currency)}
                  </span>
                  <span className="ml-2 text-gray-500">{t('additionalAnimations.perVersion')}</span>
                </div>
                <p className="mt-2 text-gray-600">{t('additionalAnimations.description')}</p>
              </div>

              {/* Volume and Bundle Savings */}
              <div className="border-t border-gray-100 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('savings.title')}
                </h3>
                <ul className="space-y-4">
                  {/* Volume Discounts */}
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-gray-600">{t('savings.volume.title')}</p>
                      <ul className="text-sm text-green-600 mt-1 pl-4 space-y-1">
                        {renderDiscountItems(pricingData.volumeDiscounts, 'volume')}
                      </ul>
                    </div>
                  </li>

                  {/* Bundle Discounts */}
                  <li className="flex items-start">
                    <svg className="h-5 w-5 text-green-500 mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <div>
                      <p className="text-gray-600">{t('savings.bundle.title')}</p>
                      <ul className="text-sm text-green-600 mt-1 pl-4 space-y-1">
                        {renderDiscountItems(pricingData.variantDiscounts, 'bundle')}
                      </ul>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 px-4 sm:px-0">
              <Link 
                to="/register" 
                className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.lg} w-full text-center justify-center block`}
              >
                {t('cta')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;